import { Input, Directive, OnInit, inject } from "@angular/core";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { Contact, PayoutAccount, PayoutProvider, Provider, Trade } from "@models";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TransactionListCoreComponent } from "../transaction-list-core.component";
import { Tradable } from "@models/interfaces/tradable";
import { SINGLE_TAG_MAX_LENGTH, TRADABLE_NAME_LENGTH_TAG } from "@helpers/constants";
import { CryptoTransaction, FiatTransaction, TradableTransaction, TransactionTag } from "@models/transaction";
import { FiatService } from "@main-services/currency";
import { ContactService } from "@main-services/contact.service";
import { PayAccountService } from "@main-services/pay_account.service";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";
import { take } from "rxjs";
import { Derivative } from "@models/derivative/currencies/derivative";
import { FiatEarn } from "@models/derivative/currencies/fiat_earn";

@Directive()
export abstract class TransactionListHeaderComponent extends TransactionListCoreComponent implements OnInit {
	@Input()
	public canOpen: boolean;

	@Input()
	public activity: string = "";

	@Input()
	public isWidget: boolean = false;

	public tagArray: string[] = [];

	private readonly statsigClientService = inject(StatsigClientService);

	public constructor(
		protected modalService: ModalService,
		protected tradableWalletListService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected fiatWalletService: FiatWalletService,
		protected fiatService: FiatService,
		protected contactService: ContactService,
		protected masterdataService: MasterdataService,
		protected payaccountService: PayAccountService,
		protected translationService: TranslationService
	) {
		super(
			modalService,
			tradableWalletListService,
			tradableListService,
			fiatWalletService,
			fiatService,
			contactService,
			payaccountService
		);
	}

	public ngOnInit(): void {
		this.statsigClientService
			.isFeatureActive(FEAT_GATES.MARGIN_TRADING_SELL)
			.pipe(take(1))
			.subscribe((sunsetEnabled) => {
				if (
					sunsetEnabled &&
					this.tradable instanceof Derivative &&
					!(this.tradable instanceof FiatEarn) &&
					!this.tradable.isRealSecurities()
				) {
					this.tagArray.push("legacy");
				}

				this.tags.forEach((tag) => {
					if (tag?.attributes?.short_name === "unstake") {
						this.tagArray.push("tag_unstake");
					} else if (tag?.attributes?.short_name === "stake") {
						this.tagArray.push("tag_stake");
					} else {
						this.tagArray.push(tag.attributes.name);
					}
				});

				if (this.containsSwap()) {
					this.tagArray.push("swap");
				}
				if (this.containsSavings()) {
					this.tagArray.push("savings");
				}
				if (this.getGiveawayReward()) {
					this.tagArray.push("transaction_label_giveaway");
				}
				if (this.transaction.isVsnFeeCollection()) {
					this.tagArray.push("vsn_fee_benefit");
				}
			});
	}

	public get amount(): number {
		return 0;
	}

	public contact(): Contact {
		if (this.transaction instanceof CryptoTransaction) {
			return this.contactService.getItemById(this.transaction.getAddressbookId()) || new Contact();
		}
		return null;
	}

	public isActivityOrHistory(): boolean {
		return this.isActivity || this.isHistory;
	}

	public getRecipientPrefix(): string {
		return this.transaction.isOutgoing() ||
			this.isIncomingDividendTransaction() ||
			this.transaction.getType() === "sell"
			? "to"
			: "from";
	}

	public get tags(): TransactionTag[] {
		return this.transaction.getTransactionTags();
	}

	private hasStatusTag(): boolean {
		return (
			this.isStatusPendingOrProcessing ||
			this.transaction.isStatus("canceled") ||
			this.transaction.isStatus("got_payment")
		);
	}

	public hasMultipleTagsPerListItem(wantActivityItem: boolean = false): boolean {
		const isActivity: boolean = wantActivityItem ? this.activity === "activity" : this.activity !== "activity";
		return (
			((this.tagArray.length === 1 && this.hasStatusTag()) ||
				this.tagArray.length > 1 ||
				this.transaction.isStatus("waiting_for_payment") ||
				this.newUserInvited) &&
			isActivity
		);
	}

	public hasLongSingleTag(): boolean {
		if (this.tagArray.length === 1) {
			return this.tagArray[0].length > SINGLE_TAG_MAX_LENGTH;
		}
		return false;
	}

	public hasLongTradableName(): boolean {
		if (this.tagArray.length === 1) {
			return this.tradableName.length > TRADABLE_NAME_LENGTH_TAG;
		}

		return false;
	}

	public isRefundTx(): boolean {
		return this.transaction.isRefund();
	}

	public getIconSuffix(): string {
		let suffix = this.transaction.hasTrade() ? this.transaction.getType() : this.transaction.getInOrOut();
		if (this.transaction.hasRecipient() && this.transaction.isConfirmationRequired()) {
			suffix += "-internal";
		}
		return suffix;
	}

	public get isHistory(): boolean {
		// Shouldnt be removed, is used by a css class for a lookup!
		return this.activity === "history";
	}

	public get isActivity(): boolean {
		// Shouldnt be removed, is used by a css class for a lookup!
		return this.activity === "activity";
	}

	public get tradableName(): string {
		if (this.tradable) {
			return this.tradable.getName();
		}
		return "";
	}

	public get currencySymbol(): string {
		if (this.tradable) {
			return this.tradable.getSymbol(false, true);
		}
		return "";
	}

	public isUncredited(): boolean {
		return this.transaction.isUncredited();
	}

	public getOutgoingIcon(): string {
		const tradable: Tradable = this.tradableListService.getItemById(this.transaction.getCurrencyId());
		if (tradable) {
			return tradable.getIconCssName(true);
		}
		return "";
	}

	public getGiveawayReward(): string {
		if (this.transaction.hasTrade()) {
			return this.transaction.getTrade().getRewardCampaign();
		}
		return "";
	}

	public hasTrade(): boolean {
		return this.transaction.hasTrade();
	}

	public trading(tradeType: string = "buy"): string {
		if (this.transaction.getTrade() && this.transaction.getTrade().getType() === tradeType) {
			return this.fiatSymbol(this.transaction.getTrade());
		}
		return this.tradableSymbol(this.transaction.getTrade());
	}

	private tradableSymbol(trade: Trade | TradableTransaction): string {
		const tradable: Tradable = this.tradableListService.getItemById(trade.getCurrencyId());
		if (tradable) {
			return tradable.getSymbol(false, true);
		}
		return "";
	}

	private fiatSymbol(trade: Trade): string {
		return this.fiatService.getItemById(trade.getFiatId()).getSymbol();
	}

	public get isWithdrawPending(): boolean {
		return this.isWithdrawal() && this.isStatusPendingOrProcessing;
	}

	public get hasCryptoTxStatusTag(): boolean {
		return (
			this.isWithdrawPending || this.transaction.getStatus() === "canceled" || this.isStatusPendingOrProcessing
		);
	}

	public get isStatusPendingOrProcessing(): boolean {
		return this.transaction.isStatus("pending") || this.transaction.isStatus("processing");
	}

	public get isTransactionPending(): boolean {
		return this.transaction.isStatus("pending") && this.transaction.hasAction(true);
	}

	public get isConfirmed(): boolean {
		return this.transaction.isConfirmed();
	}

	public get isUserInvitation(): boolean {
		return this.transaction.getStatus() === "open_invitation";
	}

	public get newUserInvited(): boolean {
		return this.isUserInvitation && this.isConfirmed;
	}

	public get isPendingUserInvitation(): boolean {
		return this.isUserInvitation && !this.isConfirmed;
	}

	public get isPending(): boolean {
		return this.isWithdrawPending || this.isStatusPendingOrProcessing || this.isPendingUserInvitation;
	}

	public getProvider(): Provider {
		const trade = this.transaction.getTrade();
		if (trade.getType() === "buy") {
			return this.masterdataService.getPaymentOptionById(trade.getPaymentOptionId());
		}
		return null;
	}

	// Related to Payments Send Fiat checks:

	public getPayoutAccount(): PayoutAccount {
		if (this.transaction instanceof FiatTransaction) {
			this.payaccountService.getItemById(this.transaction.getPayoutAccountId());
		}
		return null;
	}

	public getPayoutProvider(): PayoutProvider {
		if (this.getPayoutAccount()) {
			return this.masterdataService.getPayoutProviderById(this.getPayoutAccount().getPayoutProviderId());
		}
		if (this.transaction instanceof FiatTransaction) {
			return this.masterdataService.getPayoutProviderById(this.transaction.getPayoutProviderId());
		}
		return null;
	}

	public isFiatSend(): boolean {
		return this.getPayoutProvider() ? this.getPayoutProvider().sendEnabled(true) : false;
	}

	public get getCryptoMergerRecipientPrefix(): string {
		return this.transaction.isOutgoing() ? "from" : "to";
	}
}
